/**
 * The car field's end-of-shift HOTO message ("DCar HOTO: / Z1: 1-8 / ..."), built from
 * whoever is on a counter in the shift's final 15-minute slot.
 *
 * Pure: takes the roster's officer schedules and returns text, so it can be tested
 * without a browser. The OT section lists only OT officers (an officer's counter is
 * wanted there so the next shift knows who to relieve); a note typed beside an OT
 * officer's row rides along, and is the only way an OT officer who is NOT on a counter
 * at the end (e.g. on break till the shift ends) gets listed.
 */
import type { OfficerMetadata } from '@/types/api';
import {
  formatCounterLabel,
  getModeConfig,
  getMotorZoneRange,
  naturalSortOfficers,
} from '@/config/modeConfig';

export interface HotoBikes {
  /** Departure: DBIKES. */
  dbikes: boolean;
  /** Arrival: BIKES 1 / BIKES 2. */
  bikes1: boolean;
  bikes2: boolean;
}

export const NO_BIKES: HotoBikes = { dbikes: false, bikes1: false, bikes2: false };

export interface HotoInput {
  operationMode?: string;
  officerSchedules: Record<string, number[]>;
  officerMetadata: Record<string, OfficerMetadata>;
  /** officer key -> free-text note typed beside that officer's row. */
  notes?: Record<string, string>;
  bikes?: HotoBikes;
}

/** "1, 2, 3, 5" -> "1-3, 5"; a run of two stays "19, 20". */
export function compressCounters(nums: number[]): string {
  const sorted = [...new Set(nums)].sort((a, b) => a - b);
  const parts: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    if (j - i >= 2) parts.push(`${sorted[i]}-${sorted[j]}`);
    else for (let k = i; k <= j; k++) parts.push(`${sorted[k]}`);
    i = j + 1;
  }
  return parts.join(', ');
}

function isOtOfficer(key: string, meta?: OfficerMetadata): boolean {
  return meta ? meta.officer_type === 'OT' : key.startsWith('OT');
}

export function buildHotoMessage(input: HotoInput): string {
  const { operationMode, officerSchedules, officerMetadata, notes = {}, bikes = NO_BIKES } = input;
  const isArrival = operationMode === 'arrival';
  const config = getModeConfig(operationMode);

  // Counters with someone on them in the last slot, and who.
  const lastOf = (key: string): number => {
    const sched = officerSchedules[key] ?? [];
    return sched.length ? sched[sched.length - 1] : 0;
  };
  const keys = Object.keys(officerSchedules);
  const open = new Set<number>();
  for (const key of keys) {
    const counter = lastOf(key);
    if (counter > 0) open.add(counter);
  }

  const lines: string[] = [`${isArrival ? 'ACar' : 'DCar'} HOTO:`, ''];

  config.zones.forEach(([start, end], idx) => {
    const nums: number[] = [];
    for (let c = start + 1; c <= end; c++) if (open.has(c)) nums.push(c);
    lines.push(`Z${idx + 1}: ${nums.length ? compressCounters(nums) : '-'}`);
  });

  const [motorStart, motorEnd] = getMotorZoneRange(operationMode);
  const motorLines: string[] = [];
  for (let c = motorStart + 1; c <= motorEnd; c++) {
    if (open.has(c)) motorLines.push(formatCounterLabel(c, operationMode));
  }
  const bikeLines = isArrival
    ? [bikes.bikes1 && 'BIKES 1', bikes.bikes2 && 'BIKES 2']
    : [bikes.dbikes && 'DBIKES'];
  const extras = [...motorLines, ...bikeLines.filter((l): l is string => !!l)];
  if (extras.length) lines.push('', ...extras);

  // OT section: on a counter at the end, or carrying a note.
  const entries: string[] = [];
  let onCounter = 0;
  for (const key of naturalSortOfficers(keys)) {
    const meta = officerMetadata[key];
    if (!isOtOfficer(key, meta)) continue;
    const name = meta?.name?.trim() || key;
    const note = (notes[key] ?? '').trim();
    const counter = lastOf(key);
    if (counter > 0) {
      onCounter++;
      const label = formatCounterLabel(counter, operationMode);
      entries.push(note ? `${name} (${label}), ${note}` : `${name} ${label}`);
    } else if (note) {
      entries.push(`${name} ${note}`);
    }
  }
  if (entries.length) {
    lines.push(
      '',
      `*${isArrival ? 'AC' : 'DC'} ${String(onCounter).padStart(2, '0')} OT officer last counter*`,
      ...entries.map((e, i) => `${i + 1}) ${e}`),
    );
  }

  return lines.join('\n');
}
